import { useEffect, useMemo, useState } from 'react'
import { useDirty } from '@/hooks/useDirty'
import {
  Calculator,
  Download,
  Eye,
  Pencil,
  Plus,
  Receipt,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { downloadSchedulePdf, useReportBrand } from '@/lib/pdf'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { formatDate } from '@/lib/locale'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { StatCard } from '@/components/StatCard'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import {
  useCreatePrepaid,
  useDeletePrepaid,
  usePrepaidDetail,
  usePrepaidSchedules,
  useUpdatePrepaid,
} from '@/features/prepaid/queries'
import { EditScheduleDialog } from '@/components/EditScheduleDialog'
import { buildAmortizationSchedule } from '@/features/prepaid/calc'
import {
  PREPAID_CATEGORIES,
  type CreatePrepaidPayload,
  type PrepaidEntry,
  type PrepaidSchedule,
} from '@/features/prepaid/types'
import type { TaxonomyItem } from '@/features/coa/types'

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

function taxOption(t: TaxonomyItem): ComboboxOption<string> {
  return {
    value: t.account_code,
    label: `${t.account_code} - ${t.subcategory_name}`,
    description: t.main_category,
  }
}

const CATEGORY_OPTIONS: ComboboxOption<string>[] = PREPAID_CATEGORIES.map((c) => ({
  value: c,
  label: c,
}))

type FormState = {
  description: string
  category: string
  paymentDate: string
  amount: string
  durationMonths: string
  startDate: string
  debitAccount: string
  creditAccount: string
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function defaultForm(): FormState {
  return {
    description: '',
    category: '',
    paymentDate: todayIso(),
    amount: '',
    durationMonths: '',
    startDate: todayIso(),
    debitAccount: '',
    creditAccount: '',
  }
}

export function PrepaidPage() {
  const { toast } = useToast()
  const coa = useCoa()
  const schedules = usePrepaidSchedules()
  const posting = usePostingStatus()
  const createMut = useCreatePrepaid()
  const updateMut = useUpdatePrepaid()
  const deleteMut = useDeletePrepaid()
  const postSelectedMut = usePostSelected()

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<FormState>(() => defaultForm())
  const [preview, setPreview] = useState<PrepaidEntry[]>([])
  const [formErr, setFormErr] = useState<string | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PrepaidSchedule | null>(null)
  const [editTarget, setEditTarget] = useState<PrepaidSchedule | null>(null)
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)

  const rows = useMemo(() => schedules.data ?? [], [schedules.data])

  const isAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Income Statement')
      .map(taxOption)
  }, [coa.data])

  const bsAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Balance Sheet')
      .map(taxOption)
  }, [coa.data])

  const stats = useMemo(() => {
    const totalAmount = rows.reduce(
      (sum, s) => sum + (parseFloat(String(s.initial_amount ?? 0)) || 0),
      0,
    )
    return { count: rows.length, totalAmount }
  }, [rows])

  // Why: posting.data.unpostedSchedules is the sum across depreciation +
  // prepaid + loan schedules. Reading the global count here would make the
  // Prepaid Amortization page show "1 prepaid entry ready to post" when the
  // pending entry is actually a depreciation (or loan) one. Read only the
  // prepaid bucket so the banner matches this page's scope.
  const unpostedNum = posting.data?.unpostedSchedulesByType?.prepaid ?? 0

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // Dirty-check the create-prepaid modal. The baseline mirrors the
  // empty defaultForm() output so any field change unlocks Save. The
  // memo ensures stable identity across renders (defaultForm() returns
  // a fresh object each call). Edits go through EditPrepaidModal,
  // which owns its own dirty tracking.
  const formBaseline = useMemo(() => defaultForm(), [])
  const dirty = useDirty(form, formBaseline)

  const handleCalculate = () => {
    setFormErr(null)
    const amount = parseFloat(form.amount)
    const duration = parseInt(form.durationMonths, 10)
    if (!(amount > 0) || !(duration > 0) || !form.startDate) {
      setFormErr('Fill in the prepaid amount, duration (months), and start date.')
      setPreview([])
      return
    }
    setPreview(
      buildAmortizationSchedule({ amount, durationMonths: duration, startDate: form.startDate }),
    )
  }

  const handleSave = async () => {
    if (preview.length === 0) {
      setFormErr('Calculate the schedule first.')
      return
    }
    if (!form.debitAccount) {
      setFormErr('Expense (I/S) account is required - the debit side.')
      return
    }
    if (!form.creditAccount) {
      setFormErr('Prepaid asset (B/S) account is required - the credit side.')
      return
    }
    const payload: CreatePrepaidPayload = {
      schedule_type: 'prepaid',
      description: form.description,
      category: form.category,
      initial_amount: parseFloat(form.amount),
      start_date: form.startDate,
      purchase_date: form.paymentDate || null,
      duration_periods: parseInt(form.durationMonths, 10),
      params: {},
      debit_account: form.debitAccount,
      credit_account: form.creditAccount,
      third_account: null,
      entries: preview,
    }
    try {
      await createMut.mutateAsync(payload)
      toast({ title: 'Prepaid schedule saved.' })
      setFormOpen(false)
      setForm(defaultForm())
      setPreview([])
    } catch (err) {
      setFormErr(errorMessage(err))
    }
  }

  const runDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Schedule voided.' })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Void failed',
        description: errorMessage(err),
      })
    }
  }

  const fmtMoney = (v: number | string | null | undefined) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
    return formatCurrencyPrecise(isFinite(n) ? n : 0)
  }

  const prepaidItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.schedules ?? []
    return rows
      .filter((r) => r.scheduleType === 'prepaid')
      .map((r) => ({
        id: r.id,
        cells: [
          formatDate(r.entryDate),
          <span className="block max-w-[320px] truncate" title={r.scheduleDescription ?? undefined}>
            {r.scheduleDescription || r.category || 'Prepaid'}
          </span>,
          `Period ${r.periodNumber}`,
          <span className="text-xs text-muted-foreground">
            Dr {r.debitAccount || '-'} / Cr {r.creditAccount || '-'}
          </span>,
          fmtMoney(r.amount),
        ],
        blocked: r.blocked,
        blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
      }))
  }, [unpostedPreview.data])

  const prepaidColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Description' },
    { header: 'Period', width: 'w-24' },
    { header: 'Accounts' },
    { header: 'Amount', align: 'right' as const, width: 'w-28' },
  ]

  const runPost = async (selection: Record<string, number[]>) => {
    const ids = selection.prepaid ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'prepaid', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? ids.length} prepaid entr${(r.posted ?? ids.length) === 1 ? 'y' : 'ies'}.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  if (schedules.isLoading || coa.isLoading) {
    return (
      <Page title="Prepaid expenses">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (schedules.isError) {
    return (
      <Page title="Prepaid expenses">
        <Alert variant="destructive">
          <AlertDescription>
            Could not load schedules: {errorMessage(schedules.error)}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  return (
    <Page
      title="Prepaid expenses"
      description="Amortize prepaid expenses over time and post the monthly journal entries to the GL."
      width="wide"
      actions={
        <Button onClick={() => setFormOpen((v) => !v)}>
          {formOpen ? <X className="size-4" /> : <Plus className="size-4" />}
          {formOpen ? 'Close form' : 'New schedule'}
        </Button>
      }
    >
      <PostingBanner
        count={unpostedNum}
        label="prepaid entries"
        singularLabel="prepaid entry"
        source="prepaid"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending prepaid entries' : 'Post prepaid amortization entries'}
        description={selectorReadOnly
          ? 'These prepaid entries are queued to post.'
          : 'Tick the entries you want to post. Posting only affects the rows you select.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'prepaid',
            label: 'Prepaid',
            items: prepaidItems,
            columns: prepaidColumns,
            emptyMessage: 'No prepaid entries are pending.',
          },
        ]}
        onPost={runPost}
      />

      {/* Why: dropped the "Unposted" stat - the Post Now banner above
          already conveys it. Stats row stays consistent across the
          three allocation-schedule pages. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard label="Active schedules" value={stats.count} icon={Receipt} />
        <StatCard label="Total prepaid value" value={fmtMoney(stats.totalAmount)} icon={Receipt} />
      </div>

      {formOpen ? (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <FormSection title="Prepaid expense details">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <FormField
                  label="Description"
                  placeholder="e.g. Annual insurance premium"
                  value={form.description}
                  onChange={(e) => update('description', e.currentTarget.value)}
                />
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Category</label>
                  <Combobox
                    options={CATEGORY_OPTIONS}
                    value={form.category || null}
                    placeholder="Select category…"
                    onChange={(v) => update('category', v ?? '')}
                  />
                </div>
                <FormField
                  label="Payment date"
                  type="date"
                  value={form.paymentDate}
                  onChange={(e) => update('paymentDate', e.currentTarget.value)}
                />
                <FormField
                  label="Prepaid amount ($)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => update('amount', e.currentTarget.value)}
                />
                <FormField
                  label="Duration (months)"
                  type="number"
                  min="1"
                  placeholder="e.g. 12"
                  value={form.durationMonths}
                  onChange={(e) => update('durationMonths', e.currentTarget.value)}
                />
                <FormField
                  label="Amortization start date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update('startDate', e.currentTarget.value)}
                />
              </div>
            </FormSection>

            <FormSection title="GL accounts">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Expense account (I/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={isAccountOptions}
                    value={form.debitAccount || null}
                    placeholder="Search expense account…"
                    onChange={(v) => update('debitAccount', v ?? '')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Prepaid asset account (B/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={bsAccountOptions}
                    value={form.creditAccount || null}
                    placeholder="Search prepaid asset account…"
                    onChange={(v) => update('creditAccount', v ?? '')}
                  />
                </div>
              </div>
            </FormSection>

            {formErr ? (
              <Alert variant="destructive">
                <AlertDescription>{formErr}</AlertDescription>
              </Alert>
            ) : null}

            <Toolbar align="end">
              <Button variant="secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handleCalculate}>
                <Calculator className="size-4" /> Calculate preview
              </Button>
              {preview.length > 0 ? (
                <Button onClick={handleSave} disabled={!dirty || createMut.isPending}>
                  <Save className="size-4" /> Save schedule
                </Button>
              ) : null}
            </Toolbar>

            {preview.length > 0 ? (
              <>
                <h3 className="mb-2 text-sm font-semibold">Amortization preview</h3>
                <DataTable
                  sortable={false}
                  rows={preview}
                  columns={[
                    {
                      header: () => <span className="block text-center">#</span>,
                      accessorKey: 'period_number',
                      cell: ({ row }) => (
                        <span className="block text-center text-xs text-muted-foreground">
                          {row.original.period_number}
                        </span>
                      ),
                    },
                    {
                      header: 'Period',
                      accessorKey: 'entry_date',
                      cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                          {new Date(row.original.entry_date).toLocaleDateString()}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Amortization</span>,
                      accessorKey: 'amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.amount)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Accumulated</span>,
                      accessorKey: 'accumulated',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.accumulated)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Remaining</span>,
                      accessorKey: 'balance',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.balance)}
                        </span>
                      ),
                    },
                  ]}
                />
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h2 className="text-base font-semibold">Saved schedules</h2>
          {rows.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No prepaid schedules yet"
              description="Click New schedule to amortize your first prepaid expense."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="size-4" /> Create schedule
                </Button>
              }
            />
          ) : (
            <>
              <div className="hidden md:block">
                <DataTable
                  sortable={false}
                  rows={rows}
                  columns={[
                    { header: 'Description', accessorKey: 'description' },
                    { header: 'Category', accessorKey: 'category' },
                    {
                      header: () => <span className="block text-right">Amount</span>,
                      accessorKey: 'initial_amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.initial_amount)}
                        </span>
                      ),
                    },
                    {
                      header: 'Duration',
                      accessorKey: 'duration_periods',
                      cell: ({ row }) => `${row.original.duration_periods ?? 0} mo`,
                    },
                    {
                      header: 'Start',
                      accessorKey: 'start_date',
                      cell: ({ row }) =>
                        row.original.start_date
                          ? new Date(row.original.start_date).toLocaleDateString()
                          : '-',
                    },
                    {
                      header: 'Status',
                      accessorKey: 'status',
                      cell: ({ row }) => (
                        <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
                          {row.original.status}
                        </Badge>
                      ),
                    },
                    {
                      id: 'actions',
                      header: () => <span className="block text-right">Actions</span>,
                      cell: ({ row }) => (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setViewId(row.original.id)}
                            title="View details"
                          >
                            <Eye className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditTarget(row.original)}
                            title="Edit details"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteTarget(row.original)}
                            title="Void schedule"
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
              <div className="space-y-3 md:hidden">
                {rows.map((s) => (
                  <PrepaidCard
                    key={s.id}
                    schedule={s}
                    onView={() => setViewId(s.id)}
                    onEdit={() => setEditTarget(s)}
                    onDelete={() => setDeleteTarget(s)}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ViewPrepaidModal id={viewId} onClose={() => setViewId(null)} />

      <EditScheduleDialog
        open={editTarget !== null}
        onOpenChange={(v) => !v && setEditTarget(null)}
        schedule={editTarget ? {
          id: editTarget.id,
          description: editTarget.description,
          category: editTarget.category,
          debit_account: editTarget.debit_account,
          credit_account: editTarget.credit_account,
          third_account: editTarget.third_account,
        } : null}
        busy={updateMut.isPending}
        onSubmit={async (id, body) => {
          try {
            await updateMut.mutateAsync({ id, body: body as Partial<CreatePrepaidPayload> })
            toast({ title: 'Schedule updated.' })
            setEditTarget(null)
          } catch (err) {
            toast({ variant: 'destructive', title: 'Update failed', description: errorMessage(err) })
          }
        }}
      />

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Void schedule?"
        body={
          <p>
            This will void this prepaid schedule and create reversing entries for any periods
            already posted to the general ledger. Continue?
          </p>
        }
        confirmLabel="Void schedule"
        onConfirm={runDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={deleteMut.isPending}
      />

    </Page>
  )
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 border-b border-primary pb-1 text-sm font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h3>
      {children}
    </section>
  )
}

function PrepaidCard({
  schedule,
  onView,
  onEdit,
  onDelete,
}: {
  schedule: PrepaidSchedule
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{schedule.description || 'Untitled'}</p>
          <p className="truncate text-xs text-muted-foreground">{schedule.category || '-'}</p>
        </div>
        <Badge variant={schedule.status === 'active' ? 'success' : 'secondary'}>
          {schedule.status}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span>Amount: <strong>{fmtMoney(schedule.initial_amount)}</strong></span>
        <span>Duration: {schedule.duration_periods ?? 0} mo</span>
        <span>
          Start:{' '}
          {schedule.start_date ? new Date(schedule.start_date).toLocaleDateString() : '-'}
        </span>
      </div>
      <Toolbar align="end">
        <Button size="sm" variant="ghost" onClick={onView}>
          <Eye className="size-4" /> View
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="size-4" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="size-4 text-destructive" /> Void
        </Button>
      </Toolbar>
    </div>
  )
}

function ViewPrepaidModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const detail = usePrepaidDetail(id)
  const schedule = detail.data?.schedule
  const entries = detail.data?.entries ?? []
  const brand = useReportBrand()

  useEffect(() => {
    if (id === null) return
  }, [id])

  const onDownloadPdf = () => {
    if (!schedule) return
    downloadSchedulePdf('prepaid', schedule, entries, brand)
  }

  return (
    <Modal
      open={id !== null}
      onOpenChange={(v) => !v && onClose()}
      title={schedule?.description || 'Prepaid schedule'}
      description={
        schedule
          ? `${schedule.category ?? ''} · Amount ${fmtMoney(schedule.initial_amount)} · ${schedule.duration_periods ?? 0} months`
          : undefined
      }
      className="max-w-3xl"
    >
      {detail.isLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : detail.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load schedule.</AlertDescription>
        </Alert>
      ) : entries.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No entries.</p>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <DataTable
            sortable={false}
            rows={entries}
            columns={[
              {
                header: () => <span className="block text-center">#</span>,
                accessorKey: 'period_number',
                cell: ({ row }) => (
                  <span className="block text-center text-xs text-muted-foreground">
                    {row.original.period_number}
                  </span>
                ),
              },
              {
                header: 'Period',
                accessorKey: 'entry_date',
                cell: ({ row }) => (
                  <span className="whitespace-nowrap">
                    {new Date(row.original.entry_date).toLocaleDateString()}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Amortization</span>,
                accessorKey: 'amount',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.amount)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Accumulated</span>,
                accessorKey: 'accumulated',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.accumulated)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Remaining</span>,
                accessorKey: 'balance',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.balance)}
                  </span>
                ),
              },
              {
                header: 'Posted',
                accessorKey: 'posted',
                cell: ({ row }) => (
                  <Badge variant={row.original.posted ? 'success' : 'secondary'}>
                    {row.original.posted ? 'Yes' : 'No'}
                  </Badge>
                ),
              },
            ]}
          />
        </div>
      )}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          variant="secondary"
          onClick={onDownloadPdf}
          disabled={!schedule || entries.length === 0}
        >
          <Download className="size-4" />
          Download PDF
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}
